import React, { useEffect, useState } from "react";
import API from "common-ui-components/components/utils/API";
import RecommendationsPanel from "./RecommendationsPanel";
import ZeroDayVulnerabilitiesPanel from "./ZeroDayVulnerabilitiesPanel";
import HighPriorityVulnerabilitiesPanel from "./HighPriorityVulnerabilitiesPanel";
import PortAuditPanel from "./PortAuditPanel";
import styles from "./ThreatsDashboardPanels.module.css";

const TABLE_REQUEST = { columns: [], sort: {}, search: [], filter: [] };

const fetchTablePreview = async (url, size, request = TABLE_REQUEST) => {
  const response = await API.post(`${url}?page=0&size=${size}`, request);
  if (response?.status?.success !== true) {
    throw new Error(response?.status?.message || `Unable to load ${url}`);
  }
  return {
    records: Array.isArray(response.body?.result) ? response.body.result : [],
    total: Number(response.body?.total) || 0,
  };
};

const ThreatsDashboardPanels = () => {
  const [vulnerabilities, setVulnerabilities] = useState({ records: [], total: 0, loading: true });
  const [ports, setPorts] = useState({ records: [], total: 0, loading: true });
  const [riskIndicators, setRiskIndicators] = useState({});
  const [zeroDayStatus, setZeroDayStatus] = useState([]);
  const [zeroDayRecord, setZeroDayRecord] = useState(null);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const results = await Promise.allSettled([
        fetchTablePreview("/vulnerabilities", 7, {
          ...TABLE_REQUEST,
          sort: { column: "cvssScore", order: "desc" },
          filter: [{ column: "severity", filterValue: ["Critical", "High"] }],
        }),
        fetchTablePreview("port/port-audit-summary", 7),
        API.get("vulnerabilities/risk-indicators"),
        API.get("/vulnerabilities/zero-day/graphs?view=summary"),
        fetchTablePreview("/vulnerabilities/zero-day-summary", 1),
        API.get("/vulnerabilities/overview/recommendations"),
      ]);

      if (!active) return;
      const [vulnerabilityResult, portResult, indicatorResult, statusResult, zeroDayResult, recommendationsResult] = results;

      if (vulnerabilityResult.status === "fulfilled") {
        setVulnerabilities({ ...vulnerabilityResult.value, loading: false });
      } else {
        console.error("Failed to load high-priority vulnerabilities:", vulnerabilityResult.reason);
        setVulnerabilities({ records: [], total: 0, loading: false });
      }

      if (portResult.status === "fulfilled") {
        setPorts({ ...portResult.value, loading: false });
      } else {
        console.error("Failed to load port audit preview:", portResult.reason);
        setPorts({ records: [], total: 0, loading: false });
      }

      if (indicatorResult.status === "fulfilled" && indicatorResult.value?.status?.success) {
        setRiskIndicators(indicatorResult.value.body || {});
      } else if (indicatorResult.status === "rejected") {
        console.error("Failed to load vulnerability risk indicators:", indicatorResult.reason);
      }

      if (statusResult.status === "fulfilled" && statusResult.value?.status?.success) {
        setZeroDayStatus(statusResult.value.body?.exploitStatus || []);
      } else if (statusResult.status === "rejected") {
        console.error("Failed to load zero-day status summary:", statusResult.reason);
      }

      if (zeroDayResult.status === "fulfilled") {
        setZeroDayRecord(zeroDayResult.value.records[0] || null);
      } else {
        console.error("Failed to load zero-day vulnerability preview:", zeroDayResult.reason);
      }

      if (recommendationsResult.status === "fulfilled" && recommendationsResult.value?.status?.success) {
        const body = recommendationsResult.value.body;
        const recommendationData = body?.result || body;
        setRecommendations(Array.isArray(recommendationData) ? recommendationData[0] : recommendationData || null);
      } else if (recommendationsResult.status === "rejected") {
        console.error("Failed to load threat recommendations:", recommendationsResult.reason);
      }
    };

    load();
    return () => { active = false; };
  }, []);

  return (
    <>
      <div className={styles.topGrid}>
        <RecommendationsPanel data={recommendations} />
        <ZeroDayVulnerabilitiesPanel
          riskIndicators={riskIndicators}
          exploitStatus={zeroDayStatus}
          record={zeroDayRecord}
        />
      </div>
      <div className={styles.bottomGrid}>
        <HighPriorityVulnerabilitiesPanel data={vulnerabilities} />
        <PortAuditPanel data={ports} />
      </div>
    </>
  );
};

export default ThreatsDashboardPanels;
